/**
 * Adapted from PlayCanvas Engine's gsplat-world.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import * as THREE from 'three'
import type { GSplatGraphicsAdapter } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type { GSplatFormat } from '@/plugins/gsplat/lod/GSplatFormat'
import { GSplatPickIndex } from '@/plugins/gsplat/unified/GSplatPickIndex'
import type {
  GSplatPlacement,
  GSplatPlacementInstanceStreamsStage,
} from '@/plugins/gsplat/unified/GSplatPlacement'
import {
  GSplatBudgetBalancer,
  type GSplatBudgetPlan,
} from '@/plugins/gsplat/unified/octree/GSplatBudgetBalancer'
import {
  GSplatOctree,
  type GSplatOctreeFileResource,
} from '@/plugins/gsplat/unified/octree/GSplatOctree'
import {
  type GSplatDeviceLossSource,
  GSplatOctreeInstance,
  type GSplatOctreeLodEvaluationStage,
  type GSplatOctreeReleaseReceipt,
  type GSplatOctreeTransitionStage,
  type GSplatStreamingInvalidationSink,
} from '@/plugins/gsplat/unified/octree/GSplatOctreeInstance'
import { GSplatOctreeResource } from '@/plugins/gsplat/unified/resource/GSplatOctreeResource'
import { BlockAllocator, type MemBlock } from '@/plugins/gsplat/unified/world/BlockAllocator'
import { GSplatInfo, type GSplatInfoResource } from '@/plugins/gsplat/unified/world/GSplatInfo'
import { GSplatPlacementStateTracker } from '@/plugins/gsplat/unified/world/GSplatPlacementStateTracker'
import {
  GSplatWorkBuffer,
  type GSplatWorkBufferCopyStage,
  type GSplatWorkBufferFormatStage,
  type GSplatWorkBufferOrderStage,
  type GSplatWorkBufferPassFactory,
  type GSplatWorkBufferResizeStage,
} from '@/plugins/gsplat/unified/world/GSplatWorkBuffer'
import {
  type GSplatWorldSplat,
  GSplatWorldState,
} from '@/plugins/gsplat/unified/world/GSplatWorldState'
import type { AssetId, Disposable } from '@/shared/types/core'
import type { GSplatLodRefinementMode } from '@/shared/types/gsplat'

const ALLOCATOR_GROW_MULTIPLIER = 1.15

const DEFAULT_LOD_CONFIG: GSplatWorldLodConfig = Object.freeze({
  lodUpdateDistance: 1,
  lodUpdateAngle: 0,
  lodBehindPenalty: 1,
  lodUnderfillLimit: 0,
  lodRefinementMode: 'step',
  splatBudget: 0,
})

export interface GSplatWorldLodConfig {
  readonly lodUpdateDistance: number
  readonly lodUpdateAngle: number
  readonly lodBehindPenalty: number
  readonly lodUnderfillLimit: number
  readonly lodRefinementMode?: GSplatLodRefinementMode
  readonly splatBudget: number
}

export interface GSplatWorldOptions {
  readonly readLodConfig?: () => GSplatWorldLodConfig
  readonly streamingInvalidation?: GSplatStreamingInvalidationSink
  readonly deviceLossSource?: GSplatDeviceLossSource
}

interface GSplatOctreeInstanceSignals {
  readonly instance: GSplatOctreeInstance<GSplatOctreeFileResource>
  readonly placementLodDirty: boolean
  readonly needsLodUpdate: boolean
  readonly dirtyModifiedPlacements: boolean
  readonly dirtyPlacementSetChanged: boolean
}

interface GSplatWorldPlacementEntry {
  readonly placement: GSplatPlacement
  readonly instance: GSplatOctreeInstance<GSplatOctreeFileResource> | null
}

interface GSplatOctreeStreamingRollback {
  readonly streamingStage: GSplatWorldStreamingStage
  readonly transitionStages: readonly GSplatOctreeTransitionStage[]
  readonly evaluationStages: readonly GSplatOctreeLodEvaluationStage[]
  readonly signals: readonly GSplatOctreeInstanceSignals[]
  readonly previousFramesTillFullUpdate: number
  readonly previousLodUpdateRequested: boolean
  readonly previousHasNewInstances: boolean
  readonly previousAwaitingLodUpdate: boolean
  readonly previousBudgetScale: number
  readonly previousCameraPosition: THREE.Vector3
  readonly previousCameraForward: THREE.Vector3
  readonly previousCameraFov: number
  readonly previousWorldStateDirty: boolean
  readonly previousPlacementSetChanged: boolean
  readonly previousViewLayerMask: number | null
  readonly releaseReceipts: readonly GSplatOctreeReleaseReceipt[]
  readonly releaseVersion: number | null
}

export interface GSplatWorldStreamingResult {
  readonly newVersion: boolean
  readonly overdrawDirty: boolean
  readonly sortNeeded: boolean
}

export interface GSplatWorldStreamingStage extends GSplatWorldStreamingResult {
  commit(): void
  rollback(): void
  finalize?(): void
}

export interface GSplatWorldBakeResult {
  readonly rebuilt: boolean
  readonly count: number
  readonly textureSize: number
  readonly sortNeeded: boolean
}

export interface GSplatWorldBakeStage extends GSplatWorldBakeResult {
  prepare?(): void
  commit(): void
  rollback(): void
  finalize?(): void
}

export interface GSplatWorldSortedStage {
  readonly rebuilt: boolean
  readonly count: number
  readonly textureSize: number
  /** 执行可失败的 WorkBuffer resize/order upload，不发布 version baseline。 */
  prepare(): void
  /** renderer hooks 全部成功后才发布 render-ready version。 */
  commit(): void
  rollback(): void
  finalize?(): void
}

/** Manager-owned World：持久拥有 allocator/map、versioned states 与唯一 WorkBuffer。 */
export class GSplatWorld implements Disposable {
  readonly allocator: BlockAllocator
  readonly allocationMap = new Map<number, MemBlock>()
  /** 与 Manager 1:1；不得因多个 View 共享 Resource 而复用 selection budget。 */
  readonly budgetBalancer = new GSplatBudgetBalancer()

  readonly graphicsAdapter: GSplatGraphicsAdapter
  private readonly states = new Map<number, GSplatWorldState>()
  private readonly stateResources = new Map<GSplatWorldState, readonly GSplatInfoResource[]>()
  private readonly pendingStateReleases = new Map<number, readonly GSplatOctreeReleaseReceipt[]>()
  private readonly placementStateTracker = new GSplatPlacementStateTracker()
  private placements: readonly GSplatPlacement[] = Object.freeze([])
  private placementsConfigured = false
  private placementSetDirty = false
  private placementSetChanged = false
  private worldStateDirty = false
  private readonly octreeInstances = new Map<
    GSplatPlacement,
    GSplatOctreeInstance<GSplatOctreeFileResource>
  >()
  private readonly octreeInstanceLeases = new Map<
    GSplatOctreeInstance<GSplatOctreeFileResource>,
    Disposable
  >()
  private readonly octreeInstancesToDestroy: Array<GSplatOctreeInstance<GSplatOctreeFileResource>> =
    []
  private readonly retiredOctrees = new Set<GSplatOctree<GSplatOctreeFileResource>>()
  private hasNewOctreeInstances = false
  private framesTillFullUpdate = 0
  private lodUpdateRequested = false
  private awaitingLodUpdateValue = false
  private budgetScale = 1
  private readonly lastLodCameraPosition = new THREE.Vector3(
    Number.POSITIVE_INFINITY,
    Number.POSITIVE_INFINITY,
    Number.POSITIVE_INFINITY
  )
  private readonly lastLodCameraForward = new THREE.Vector3(
    Number.POSITIVE_INFINITY,
    Number.POSITIVE_INFINITY,
    Number.POSITIVE_INFINITY
  )
  private lastLodCameraFov = -1
  private readonly readLodConfig: () => GSplatWorldLodConfig
  private readonly externalStreamingInvalidation?: GSplatStreamingInvalidationSink
  private readonly deviceLossSource?: GSplatDeviceLossSource
  private suppressInternalInstanceInvalidation = false
  private workBufferValue: GSplatWorkBuffer
  private lastVersionValue = 0
  private currentVersionValue = 0
  private lastStreamedVersion = 0
  private lastBakedVersion = 0
  private bufferCopyUploadedValue = 0
  private bufferCopyTotalValue = 0
  private readonly colorUpdateRatio: number
  private readonly lastColorCameraPosition = new THREE.Vector3(
    Number.POSITIVE_INFINITY,
    Number.POSITIVE_INFINITY,
    Number.POSITIVE_INFINITY
  )
  private destroyed = false
  /** 最近一次成功发布的 View 相机 layer mask；每个 View 的 World 独立维护。 */
  private viewLayerMask: number | null = null

  constructor(
    adapter: GSplatGraphicsAdapter,
    format: GSplatFormat,
    initialCapacity = 0,
    passFactory?: GSplatWorkBufferPassFactory,
    colorUpdateAngleDegrees = 1,
    options: GSplatWorldOptions = {}
  ) {
    if (!Number.isFinite(colorUpdateAngleDegrees) || colorUpdateAngleDegrees < 0) {
      throw new Error(`非法 GSplat colorUpdateAngle: ${colorUpdateAngleDegrees}`)
    }
    if (format.graphicsAdapter !== adapter) {
      throw new Error('GSplat World format 与 adapter 不属于同一 graphics device')
    }
    this.graphicsAdapter = adapter
    this.readLodConfig = options.readLodConfig ?? (() => DEFAULT_LOD_CONFIG)
    this.externalStreamingInvalidation = options.streamingInvalidation
    this.deviceLossSource = options.deviceLossSource
    this.colorUpdateRatio = Math.tan(THREE.MathUtils.degToRad(colorUpdateAngleDegrees))
    this.allocator = new BlockAllocator(initialCapacity, ALLOCATOR_GROW_MULTIPLIER)
    this.workBufferValue = new GSplatWorkBuffer(adapter, format, passFactory)
  }

  private get adapter(): GSplatGraphicsAdapter {
    return this.graphicsAdapter
  }

  get isDestroyed(): boolean {
    return this.destroyed
  }

  get workBuffer(): GSplatWorkBuffer {
    return this.workBufferValue
  }

  get currentVersion(): number {
    return this.currentVersionValue
  }

  get lastWorldStateVersion(): number {
    return this.lastVersionValue
  }

  get bufferCopyUploaded(): number {
    return this.bufferCopyUploadedValue
  }

  get bufferCopyTotal(): number {
    return this.bufferCopyTotalValue
  }

  get awaitingLodUpdate(): boolean {
    return this.awaitingLodUpdateValue
  }

  get pendingLoadCount(): number {
    let count = 0
    for (const instance of this.octreeInstances.values()) count += instance.pendingLoadCount
    return count
  }

  get currentState(): GSplatWorldState | undefined {
    return this.states.get(this.currentVersionValue)
  }

  get hasOctreeInstances(): boolean {
    return this.octreeInstances.size > 0
  }

  /** on-demand FrameDriver 在 load/cooldown/retirement 收敛前必须继续提供 logical frame。 */
  get needsStreamingFrame(): boolean {
    if (this.pendingLoadCount > 0 || this.octreeInstancesToDestroy.length > 0) return true
    for (const instance of this.octreeInstances.values()) {
      if (instance.octree.hasPendingCooldowns) return true
    }
    for (const octree of this.retiredOctrees) {
      if (octree.hasPendingCooldowns) return true
    }
    return false
  }

  getState(version: number): GSplatWorldState | undefined {
    return this.states.get(version)
  }

  /**
   * Director 会把同一 Layer 的 Placement 交给每个 View 的 Manager；这里复用 World
   * 的可见性谓词，避免仅凭 membership 把另一个 View 的 shader effect 一并打开。
   */
  isAssetVisibleForCamera(assetId: AssetId, camera: THREE.Camera): boolean {
    const viewLayerMask = camera.layers.mask
    const matches = (placement: GSplatPlacement): boolean =>
      placement.assetId === assetId && isPlacementVisibleForLayerMask(placement, viewLayerMask)
    if (this.placements.some(matches)) return true
    for (const placement of this.octreeInstances.keys()) {
      if (matches(placement)) return true
    }
    return false
  }

  /** 按冻结 PlayCanvas 结构拆分静态 Placement 与每 Manager 独占 OctreeInstance。 */
  reconcile(placements: readonly GSplatPlacement[]): void {
    this.assertActive()
    const nextStaticPlacements: GSplatPlacement[] = []
    const nextOctreePlacements = new Set<GSplatPlacement>()
    const stagedInstances = new Map<
      GSplatPlacement,
      GSplatOctreeInstance<GSplatOctreeFileResource>
    >()
    const stagedLeases = new Map<GSplatOctreeInstance<GSplatOctreeFileResource>, Disposable>()
    try {
      for (const placement of placements) {
        if (!isPlacementUsable(placement)) continue
        const resource = placement.resource
        const resourceAdapter = resource?.graphicsAdapter ?? resource?.format?.graphicsAdapter
        if (resourceAdapter && resourceAdapter !== this.adapter) {
          throw new Error('GSplat Placement Resource 与 World adapter 不属于同一 graphics device')
        }
        if (
          resource?.graphicsAdapter &&
          resource.format &&
          resource.graphicsAdapter !== resource.format.graphicsAdapter
        ) {
          throw new Error('GSplat Placement Resource 的 adapter 与 format 不一致')
        }
        const octree = this.resolvePlacementOctree(placement)
        if (!octree) {
          if (!nextStaticPlacements.includes(placement)) nextStaticPlacements.push(placement)
          continue
        }
        if (octree.isDestroyed) continue
        nextOctreePlacements.add(placement)
        const existing = this.octreeInstances.get(placement)
        if (existing?.octree === octree || stagedInstances.has(placement)) continue
        const lease = placement.resource?.acquire?.()
        if (!lease) throw new Error('GSplat Octree Placement Resource 不支持独立生命周期 lease')
        let instance: GSplatOctreeInstance<GSplatOctreeFileResource> | null = null
        try {
          instance = new GSplatOctreeInstance(
            octree,
            placement,
            this.createInstanceInvalidationSink(),
            { deviceLossSource: this.deviceLossSource }
          )
          stagedInstances.set(placement, instance)
          stagedLeases.set(instance, lease)
        } catch (error) {
          instance?.destroy()
          lease.dispose()
          throw error
        }
      }
    } catch (error) {
      for (const staged of stagedInstances.values()) {
        staged.destroy()
        stagedLeases.get(staged)?.dispose()
      }
      throw error
    }
    const changed =
      !this.placementsConfigured ||
      nextStaticPlacements.length !== this.placements.length ||
      nextStaticPlacements.some((placement, index) => placement !== this.placements[index]) ||
      stagedInstances.size > 0 ||
      [...this.octreeInstances.keys()].some((placement) => !nextOctreePlacements.has(placement))
    this.placementsConfigured = true
    if (!changed) {
      for (const staged of stagedInstances.values()) {
        staged.destroy()
        stagedLeases.get(staged)?.dispose()
      }
      return
    }

    for (const [placement, instance] of [...this.octreeInstances]) {
      const replacement = stagedInstances.get(placement)
      if (nextOctreePlacements.has(placement) && !replacement) continue
      this.octreeInstances.delete(placement)
      this.octreeInstancesToDestroy.push(instance)
    }
    for (const [placement, instance] of stagedInstances) {
      this.octreeInstances.set(placement, instance)
      this.octreeInstanceLeases.set(instance, stagedLeases.get(instance)!)
    }
    if (stagedInstances.size > 0) this.hasNewOctreeInstances = true
    this.placements = Object.freeze(nextStaticPlacements)
    this.placementSetDirty = true
    this.placementSetChanged = true
  }

  createState(
    splats: readonly GSplatWorldSplat[],
    pickIndex: GSplatPickIndex = GSplatPickIndex.empty()
  ): GSplatWorldState {
    this.assertActive()
    const version = this.lastVersionValue + 1
    const allocatorCheckpoint = this.allocator.createCheckpoint()
    const allocationCheckpoint = [...this.allocationMap]
    let state: GSplatWorldState
    try {
      state = new GSplatWorldState(
        version,
        splats,
        this.allocator,
        this.allocationMap,
        this.adapter.limits.maxTextureDimension2D,
        pickIndex
      )
    } catch (error) {
      this.allocator.restoreCheckpoint(allocatorCheckpoint)
      this.allocationMap.clear()
      for (const [allocId, block] of allocationCheckpoint) this.allocationMap.set(allocId, block)
      throw error
    }
    this.states.set(version, state)
    this.lastVersionValue = version
    return state
  }

  /**
   * 为 CPU/GPU 排序结果建立 version-aware 两阶段提交。
   *
   * 丢失的 version 或早于当前 render-ready version 的结果只返回 null，由
   * Manager 归还 Worker order lease，不修改 World。
   */
  stageSorted(
    version: number,
    count: number,
    _camera: THREE.Camera,
    _updateBounds: boolean,
    orderData?: Uint32Array
  ): GSplatWorldSortedStage | null {
    this.assertActive()
    const state = this.states.get(version)
    if (!state || version < this.currentVersionValue) return null
    if (!Number.isSafeInteger(count) || count < 0 || count > state.totalActiveSplats) {
      throw new Error(
        `GSplat sorted count 非法：version=${version}, actual=${count}, limit=${state.totalActiveSplats}`
      )
    }
    if (orderData && orderData.length < count) {
      throw new Error(
        `GSplat order 长度不足：version=${version}, actual=${orderData.length}, required=${count}`
      )
    }

    const previousCurrentVersion = this.currentVersionValue
    const previousLastBakedVersion = this.lastBakedVersion
    const previousSortedBefore = state.sortedBefore
    const previousColorCameraPosition = this.lastColorCameraPosition.clone()
    const nextColorCameraPosition = new THREE.Vector3()
    const previousBufferCopyUploaded = this.bufferCopyUploadedValue
    const previousBufferCopyTotal = this.bufferCopyTotalValue
    const rebuilt = !state.sortedBefore
    const infoSplats = state.splats.filter(
      (splat): splat is GSplatInfo => splat instanceof GSplatInfo
    )
    const updatePlans = rebuilt ? infoSplats.map((splat) => ({ splat, plan: splat.update() })) : []
    const copyRequirements = rebuilt
      ? this.collectCopyRequirements(state, version)
      : { fullRebuild: false, allocIds: new Set<number>(), splats: [] }
    let resizeStage: GSplatWorkBufferResizeStage | null = null
    let orderStage: GSplatWorkBufferOrderStage | null = null
    let copyStage: GSplatWorkBufferCopyStage | null = null
    let baselinesCommitted = false
    let prepared = false
    let committed = false
    let finalized = false

    return {
      rebuilt,
      count,
      textureSize: state.textureSize,
      prepare: () => {
        if (prepared) return
        if (this.workBufferValue.textureSize !== state.textureSize) {
          resizeStage = this.workBufferValue.stageResize(state.textureSize)
          resizeStage?.commit()
        }
        if (_updateBounds) {
          this.workBufferValue.frustumCuller.updateBoundsData(state.boundsGroups)
          this.workBufferValue.frustumCuller.updateTransformsData(state.boundsGroups)
        }
        const splatsToCopy = copyRequirements.fullRebuild
          ? infoSplats
          : copyRequirements.splats.filter(
              (splat): splat is GSplatInfo => splat instanceof GSplatInfo
            )
        if (splatsToCopy.length > 0) {
          copyStage = this.workBufferValue.stageCopy('full', {
            splats: splatsToCopy,
            camera: _camera,
            changedAllocIds: copyRequirements.fullRebuild ? null : copyRequirements.allocIds,
          })
        }
        if (orderData) {
          orderStage = this.workBufferValue.stageOrderData(orderData)
        }
        prepared = true
      },
      commit: () => {
        if (committed) return
        if (!prepared) throw new Error('GSplat sorted stage 尚未 prepare')
        copyStage?.commit()
        for (const { splat, plan } of updatePlans) splat.commitUpdateBaseline(plan)
        baselinesCommitted = true
        _camera.getWorldPosition(nextColorCameraPosition)
        // order backing 在普通排序中保持稳定。上传是本 transaction 最后一个可失败步骤；
        // 成功后只发布内存状态，因而无需每帧复制整块 order buffer 来支持回滚。
        orderStage?.commit()
        state.sortedBefore = true
        this.currentVersionValue = version
        this.lastBakedVersion = version
        this.lastColorCameraPosition.copy(nextColorCameraPosition)
        this.bufferCopyUploadedValue += copyRequirements.fullRebuild
          ? this.allocationMap.size
          : copyRequirements.allocIds.size
        this.bufferCopyTotalValue = this.allocationMap.size
        committed = true
      },
      rollback: () => {
        if (finalized) return
        if (baselinesCommitted) {
          for (let index = updatePlans.length - 1; index >= 0; index--) {
            const { splat, plan } = updatePlans[index]
            splat.rollbackUpdateBaseline(plan)
          }
          baselinesCommitted = false
        }
        if (committed) {
          state.sortedBefore = previousSortedBefore
          this.currentVersionValue = previousCurrentVersion
          this.lastBakedVersion = previousLastBakedVersion
          this.lastColorCameraPosition.copy(previousColorCameraPosition)
          this.bufferCopyUploadedValue = previousBufferCopyUploaded
          this.bufferCopyTotalValue = previousBufferCopyTotal
        }
        copyStage?.rollback()
        orderStage?.rollback()
        resizeStage?.rollback()
        committed = false
        prepared = false
      },
      finalize: () => {
        if (!committed || finalized) return
        copyStage?.finalize()
        orderStage?.finalize()
        resizeStage?.finalize()
        this.commitPendingStateReleases(version)
        if (version > previousCurrentVersion) {
          this.cleanupOldWorldStates(previousCurrentVersion, version)
        }
        finalized = true
      },
    }
  }

  /** 保留给 World 单测/内部调用的同步 GPU 便捷入口。 */
  markSorted(version: number): void {
    const state = this.states.get(version)
    if (!state) throw new Error(`GSplat WorldState 不存在: ${version}`)
    const stage = this.stageSorted(
      version,
      state.totalActiveSplats,
      new THREE.PerspectiveCamera(),
      true
    )
    if (!stage) return
    try {
      stage.prepare()
      stage.commit()
    } catch (error) {
      stage.rollback()
      throw error
    }
    stage.finalize?.()
  }

  syncFormat(format: GSplatFormat): boolean {
    this.assertActive()
    const stage = this.stageFormat(format)
    if (!stage) return false
    try {
      stage.commit()
    } catch (error) {
      stage.rollback()
      throw error
    }
    stage.finalize()
    return true
  }

  stageFormatSelection(format: GSplatFormat): GSplatWorkBufferFormatStage | null {
    this.assertActive()
    return this.stageFormat(format)
  }

  stageActiveFormatSync(): GSplatWorkBufferFormatStage | null {
    this.assertActive()
    return this.stageFormat(this.workBufferValue.format)
  }

  stageStreamingUpdate(
    camera: THREE.Camera,
    allowLodUpdate: boolean,
    requireCenters: boolean,
    paramsDirty = false
  ): GSplatWorldStreamingStage {
    this.assertActive()
    if (this.placementsConfigured) {
      return this.stageOctreeStreamingUpdate(camera, allowLodUpdate, requireCenters, paramsDirty)
    }
    const nextVersion = this.lastVersionValue
    const newVersion = nextVersion !== this.lastStreamedVersion
    const previousVersion = this.lastStreamedVersion
    let committed = false
    return {
      newVersion,
      overdrawDirty: false,
      sortNeeded: newVersion,
      commit: () => {
        if (committed) return
        this.lastStreamedVersion = nextVersion
        committed = true
      },
      rollback: () => {
        if (!committed) return
        this.lastStreamedVersion = previousVersion
        committed = false
      },
    }
  }

  private stageOctreeStreamingUpdate(
    camera: THREE.Camera,
    allowLodUpdate: boolean,
    requireCenters: boolean,
    paramsDirty: boolean
  ): GSplatWorldStreamingStage {
    this.sanitizeOctreeInstances()
    const config = this.validateLodConfig(this.readLodConfig())
    const previousFramesTillFullUpdate = this.framesTillFullUpdate
    const previousLodUpdateRequested = this.lodUpdateRequested
    const previousHasNewInstances = this.hasNewOctreeInstances
    const previousAwaitingLodUpdate = this.awaitingLodUpdateValue
    const previousBudgetScale = this.budgetScale
    const previousCameraPosition = this.lastLodCameraPosition.clone()
    const previousCameraForward = this.lastLodCameraForward.clone()
    const previousCameraFov = this.lastLodCameraFov
    const previousWorldStateDirty = this.worldStateDirty
    const previousPlacementSetChanged = this.placementSetChanged
    const previousViewLayerMask = this.viewLayerMask
    const nextViewLayerMask = camera.layers.mask
    const signals = [...this.octreeInstances.values()].map((instance) =>
      this.captureInstanceSignals(instance)
    )
    const transitionStages: GSplatOctreeTransitionStage[] = []
    const evaluationStages: GSplatOctreeLodEvaluationStage[] = []
    const releaseReceipts: GSplatOctreeReleaseReceipt[] = []
    const retiringInstances = [...this.octreeInstancesToDestroy]
    let budgetPlan: GSplatBudgetPlan | null = null
    let stateStage: GSplatWorldStreamingStage | null = null
    let plannedBudgetScale = this.budgetScale
    let plannedFramesTillFullUpdate = this.framesTillFullUpdate - 1
    let plannedLodUpdateRequested = this.lodUpdateRequested
    if (plannedFramesTillFullUpdate <= 0) {
      plannedFramesTillFullUpdate = 10
      plannedLodUpdateRequested = true
    }
    const fullUpdate = plannedLodUpdateRequested && allowLodUpdate
    if (fullUpdate) plannedLodUpdateRequested = false
    const hasNewInstances = this.hasNewOctreeInstances && allowLodUpdate
    const plannedHasNewInstances = hasNewInstances ? false : this.hasNewOctreeInstances
    let plannedAwaitingLodUpdate = fullUpdate ? false : this.awaitingLodUpdateValue
    let cameraBaseline: {
      readonly position: THREE.Vector3
      readonly forward: THREE.Vector3
      readonly fov: number
    } | null = null
    let lodEvaluated = false
    let committed = false
    let settled = false
    let releaseVersion: number | null = null

    try {
      let instanceModified = false
      let instanceSetChanged = false
      let anyInstanceNeedsLodUpdate = false

      if (fullUpdate) {
        for (const signal of signals) this.clearConsumedInstanceSignals(signal)
        for (const signal of signals) {
          const completion = signal.instance.stageCompletions()
          transitionStages.push(completion)
          instanceModified ||= signal.dirtyModifiedPlacements || completion.changed
          instanceSetChanged ||=
            signal.dirtyPlacementSetChanged || signal.instance.dirtyPlacementSetChanged
          anyInstanceNeedsLodUpdate ||=
            signal.placementLodDirty || signal.needsLodUpdate || signal.instance.needsLodUpdate
        }
      }

      let anyOctreeMoved = false
      let cameraMovedForLod = false
      if (fullUpdate) {
        for (const instance of this.octreeInstances.values()) {
          anyOctreeMoved ||= instance.testMoved(config.lodUpdateDistance)
        }
        cameraMovedForLod = this.testCameraMovedForLod(camera, config)
      }

      if (paramsDirty) {
        if (this.octreeInstances.size > 0) plannedAwaitingLodUpdate = true
      }

      lodEvaluated =
        cameraMovedForLod ||
        anyOctreeMoved ||
        paramsDirty ||
        anyInstanceNeedsLodUpdate ||
        hasNewInstances
      if (lodEvaluated && this.octreeInstances.size > 0) {
        camera.updateWorldMatrix(true, false)
        cameraBaseline = {
          position: camera.getWorldPosition(new THREE.Vector3()),
          forward: camera.getWorldDirection(new THREE.Vector3()),
          fov: camera instanceof THREE.PerspectiveCamera ? camera.fov : 0,
        }
        const lodParams = {
          lodBehindPenalty: config.lodBehindPenalty,
          lodUnderfillLimit: config.lodUnderfillLimit,
          lodRefinementMode: config.lodRefinementMode ?? 'step',
        }
        if (config.splatBudget > 0) {
          const maxDistance = this.computeGlobalMaxDistance(camera)
          let totalOptimalSplats = 0
          let paddingEstimate = this.computeStaticPaddingEstimate()
          for (const instance of this.octreeInstances.values()) {
            const evaluation = instance.stageOptimalLods(
              camera,
              lodParams,
              this.budgetScale,
              maxDistance
            )
            evaluationStages.push(evaluation)
            totalOptimalSplats += evaluation.totalSplats
            for (const placement of instance.activePlacements) {
              const count = this.readPlacementSplatCount(placement)
              paddingEstimate += this.rowPadding(count)
            }
          }
          const fixedSplats = this.placements.reduce(
            (total, placement) => total + this.readPlacementSplatCount(placement),
            0
          )
          const octreeBudget = Math.max(1, config.splatBudget - fixedSplats)
          const adjustedBudget = Math.max(1, octreeBudget - paddingEstimate)
          if (totalOptimalSplats > 0) {
            const ratio = totalOptimalSplats / adjustedBudget
            if (ratio > 1.4 || ratio < 0.6) {
              const correction = 1 / Math.sqrt(ratio)
              plannedBudgetScale = THREE.MathUtils.clamp(
                this.budgetScale * (1 + (correction - 1) * 0.3),
                0.01,
                100
              )
            }
          }
          const preliminaryPlan = this.budgetBalancer.balance(this.octreeInstances, adjustedBudget)
          const overrides = new Map<GSplatOctreeInstance<GSplatOctreeFileResource>, number[]>()
          for (const instance of this.octreeInstances.values()) {
            overrides.set(
              instance,
              instance.nodeInfos.map((nodeInfo) => nodeInfo.optimalLod)
            )
          }
          for (const entry of preliminaryPlan.entries) {
            const instance = entry.instance as GSplatOctreeInstance<GSplatOctreeFileResource>
            const nodeIndex = instance.nodeInfos.indexOf(
              entry.nodeInfo as (typeof instance.nodeInfos)[number]
            )
            if (nodeIndex < 0) throw new Error('GSplat budget plan NodeInfo 不属于对应 Instance')
            overrides.get(instance)![nodeIndex] = entry.nextLod
          }
          for (const instance of this.octreeInstances.values()) {
            transitionStages.push(
              instance.stageLodChanges(
                instance.octree.lodLevels - 1,
                lodParams,
                overrides.get(instance)
              )
            )
          }
          budgetPlan = this.budgetBalancer.rebasePlan(preliminaryPlan)
        } else {
          plannedBudgetScale = 1
          for (const instance of this.octreeInstances.values()) {
            transitionStages.push(instance.stageLodUpdate(camera, lodParams))
          }
        }
      }

      for (const instance of this.octreeInstances.values()) {
        instanceModified ||= instance.dirtyModifiedPlacements
        instanceSetChanged ||= instance.dirtyPlacementSetChanged
      }
      const entries = this.collectPlacementEntries()
      const forceWorldState =
        previousWorldStateDirty ||
        paramsDirty ||
        previousViewLayerMask !== nextViewLayerMask ||
        this.placementSetDirty ||
        instanceModified ||
        instanceSetChanged ||
        this.states.size === 0
      stateStage = this.stagePlacementStreamingUpdate(
        requireCenters,
        entries,
        nextViewLayerMask,
        forceWorldState,
        paramsDirty || this.placementSetChanged || instanceSetChanged
      )
      if (stateStage.newVersion) {
        for (const instance of this.octreeInstances.values()) {
          releaseReceipts.push(instance.stagePendingReleases())
        }
        for (const instance of retiringInstances) {
          releaseReceipts.push(this.stageInstanceRetirement(instance))
        }
      }
    } catch (error) {
      stateStage?.rollback()
      for (let index = transitionStages.length - 1; index >= 0; index--) {
        transitionStages[index].rollback()
      }
      for (let index = evaluationStages.length - 1; index >= 0; index--) {
        evaluationStages[index].rollback()
      }
      for (const signal of signals) this.restoreInstanceSignals(signal)
      for (let index = releaseReceipts.length - 1; index >= 0; index--) {
        releaseReceipts[index].rollback()
      }
      this.worldStateDirty = previousWorldStateDirty
      throw error
    }

    const streamingStage = stateStage
    const plannedReleaseReceipts = Object.freeze([...releaseReceipts])
    return {
      newVersion: streamingStage.newVersion,
      overdrawDirty: paramsDirty,
      sortNeeded: paramsDirty || streamingStage.sortNeeded,
      commit: () => {
        if (settled) throw new Error('GSplat World streaming stage 已结束')
        if (committed) return
        try {
          for (const stage of evaluationStages) stage.commit()
          for (const stage of transitionStages) stage.commit()
          streamingStage.commit()
          if (streamingStage.newVersion && plannedReleaseReceipts.length > 0) {
            releaseVersion = this.lastVersionValue
            if (this.pendingStateReleases.has(releaseVersion)) {
              throw new Error(`GSplat WorldState release receipt 重复: ${releaseVersion}`)
            }
            this.pendingStateReleases.set(releaseVersion, plannedReleaseReceipts)
          }
          this.framesTillFullUpdate = plannedFramesTillFullUpdate
          this.lodUpdateRequested = plannedLodUpdateRequested
          this.hasNewOctreeInstances = plannedHasNewInstances
          this.awaitingLodUpdateValue = plannedAwaitingLodUpdate
          this.budgetScale = plannedBudgetScale
          if (cameraBaseline) {
            this.lastLodCameraPosition.copy(cameraBaseline.position)
            this.lastLodCameraForward.copy(cameraBaseline.forward)
            this.lastLodCameraFov = cameraBaseline.fov
            for (const instance of this.octreeInstances.values()) instance.updateMoved()
          }
          if (streamingStage.newVersion) {
            this.worldStateDirty = false
            this.placementSetChanged = false
            for (const instance of this.octreeInstances.values()) {
              instance.dirtyModifiedPlacements = false
              instance.dirtyPlacementSetChanged = false
            }
          }
          this.viewLayerMask = nextViewLayerMask
          if (lodEvaluated) {
            for (const instance of this.octreeInstances.values()) {
              instance.needsLodUpdate = false
              instance.placement.lodDirty = false
            }
          }
          committed = true
        } catch (error) {
          this.rollbackOctreeStreamingStage({
            streamingStage,
            transitionStages,
            evaluationStages,
            signals,
            previousFramesTillFullUpdate,
            previousLodUpdateRequested,
            previousHasNewInstances,
            previousAwaitingLodUpdate,
            previousBudgetScale,
            previousCameraPosition,
            previousCameraForward,
            previousCameraFov,
            previousWorldStateDirty,
            previousPlacementSetChanged,
            previousViewLayerMask,
            releaseReceipts: plannedReleaseReceipts,
            releaseVersion,
          })
          settled = true
          throw error
        }
      },
      rollback: () => {
        if (settled) return
        this.rollbackOctreeStreamingStage({
          streamingStage,
          transitionStages,
          evaluationStages,
          signals,
          previousFramesTillFullUpdate,
          previousLodUpdateRequested,
          previousHasNewInstances,
          previousAwaitingLodUpdate,
          previousBudgetScale,
          previousCameraPosition,
          previousCameraForward,
          previousCameraFov,
          previousWorldStateDirty,
          previousPlacementSetChanged,
          previousViewLayerMask,
          releaseReceipts: plannedReleaseReceipts,
          releaseVersion,
        })
        committed = false
        settled = true
      },
      finalize: () => {
        if (settled) return
        if (!committed) {
          streamingStage.rollback()
          for (let index = transitionStages.length - 1; index >= 0; index--) {
            transitionStages[index].rollback()
          }
          for (let index = evaluationStages.length - 1; index >= 0; index--) {
            evaluationStages[index].rollback()
          }
          for (const signal of signals) this.restoreInstanceSignals(signal)
          for (let index = plannedReleaseReceipts.length - 1; index >= 0; index--) {
            plannedReleaseReceipts[index].rollback()
          }
          settled = true
          return
        }
        let failure: unknown
        const finalize = (operation: () => void): void => {
          try {
            operation()
          } catch (error) {
            failure ??= error
          }
        }
        if (budgetPlan) finalize(() => this.budgetBalancer.commitBalance(budgetPlan!))
        finalize(() => streamingStage.finalize?.())
        this.suppressInternalInstanceInvalidation = true
        try {
          for (const stage of evaluationStages) finalize(() => stage.finalize())
          for (const stage of transitionStages) finalize(() => stage.finalize())
        } finally {
          this.suppressInternalInstanceInvalidation = false
        }
        settled = true
        if (failure) throw failure
      },
    }
  }

  stageBake(version: number, _camera: THREE.Camera, _updateBounds: boolean): GSplatWorldBakeStage {
    this.assertActive()
    const state = this.states.get(version)
    const infoSplats =
      state?.sortedBefore === true
        ? state.splats.filter((splat): splat is GSplatInfo => splat instanceof GSplatInfo)
        : []
    const updatePlans = infoSplats
      .map((splat) => ({ splat, plan: splat.update() }))
      .filter(({ plan }) => plan.changed)
    const updatedSplats = new Set(updatePlans.map(({ splat }) => splat))
    const cameraPosition = _camera.getWorldPosition(new THREE.Vector3())
    const previousCameraPosition = this.lastColorCameraPosition.clone()
    const previousBufferCopyUploaded = this.bufferCopyUploadedValue
    const previousBufferCopyTotal = this.bufferCopyTotalValue
    const translationDelta = Number.isFinite(previousCameraPosition.x)
      ? previousCameraPosition.distanceTo(cameraPosition)
      : 0
    const changedColorAllocIds = new Set<number>()
    const splatsWithSH: GSplatInfo[] = []
    const standaloneAccumulations: Array<{
      readonly splat: GSplatInfo
      readonly previous: number
      readonly next: number
    }> = []
    const nodeAccumulations: Array<{
      readonly nodeInfo: { colorAccumulatedTranslation: number }
      readonly previous: number
      readonly next: number
    }> = []
    const resetStandaloneAccumulations: Array<{
      readonly splat: GSplatInfo
      readonly previous: number
    }> = []
    const resetNodeAccumulations: Array<{
      readonly nodeInfo: { colorAccumulatedTranslation: number }
      readonly previous: number
    }> = []
    for (const { splat } of updatePlans) {
      if (splat.nodeInfos) {
        for (const nodeIndex of splat.intervalNodeIndices) {
          const nodeInfo = splat.nodeInfos[nodeIndex]
          resetNodeAccumulations.push({
            nodeInfo,
            previous: nodeInfo.colorAccumulatedTranslation,
          })
        }
      } else {
        resetStandaloneAccumulations.push({
          splat,
          previous: splat.colorAccumulatedTranslation,
        })
      }
    }
    if (translationDelta > 0) {
      for (const splat of infoSplats) {
        if (updatedSplats.has(splat) || !splat.hasSphericalHarmonics) continue
        splatsWithSH.push(splat)
        if (splat.nodeInfos) {
          for (let index = 0; index < splat.intervalNodeIndices.length; index++) {
            const nodeInfo = splat.nodeInfos[splat.intervalNodeIndices[index]]
            const previous = nodeInfo.colorAccumulatedTranslation
            let next = previous + translationDelta
            const threshold = this.colorUpdateRatio * Math.max(1, nodeInfo.worldDistance)
            if (next >= threshold) {
              changedColorAllocIds.add(splat.intervalAllocIds[index])
              next = 0
            }
            nodeAccumulations.push({ nodeInfo, previous, next })
          }
        } else {
          const previous = splat.colorAccumulatedTranslation
          let next = previous + translationDelta
          const inverse = splat.node.matrixWorld.clone().invert()
          const localCamera = cameraPosition.clone().applyMatrix4(inverse)
          const closest = splat.aabb.clampPoint(localCamera, new THREE.Vector3())
          const scale = new THREE.Vector3().setFromMatrixScale(splat.node.matrixWorld).x
          const threshold =
            this.colorUpdateRatio * Math.max(1, localCamera.distanceTo(closest) * scale)
          if (next >= threshold) {
            changedColorAllocIds.add(splat.allocId)
            next = 0
          }
          standaloneAccumulations.push({ splat, previous, next })
        }
      }
    }
    let fullCopyStage: GSplatWorkBufferCopyStage | null = null
    let colorCopyStage: GSplatWorkBufferCopyStage | null = null
    let prepared = false
    let baselinesCommitted = false
    let accumulationsCommitted = false
    let committed = false
    let finalized = false
    return {
      rebuilt: false,
      count: 0,
      textureSize: 0,
      sortNeeded: updatePlans.length > 0,
      prepare: () => {
        if (prepared) return
        if (_updateBounds && state) {
          this.workBufferValue.frustumCuller.updateTransformsData(state.boundsGroups)
        }
        if (updatePlans.length > 0) {
          fullCopyStage = this.workBufferValue.stageCopy('full', {
            splats: updatePlans.map(({ splat }) => splat),
            camera: _camera,
            changedAllocIds: null,
          })
        }
        if (changedColorAllocIds.size > 0) {
          colorCopyStage = this.workBufferValue.stageCopy('colorOnly', {
            splats: splatsWithSH,
            camera: _camera,
            changedAllocIds: changedColorAllocIds,
          })
        }
        prepared = true
      },
      commit: () => {
        if (committed) return
        if (!prepared) throw new Error('GSplat bake stage 尚未 prepare')
        fullCopyStage?.commit()
        colorCopyStage?.commit()
        for (const { splat, plan } of updatePlans) splat.commitUpdateBaseline(plan)
        baselinesCommitted = true
        for (const entry of resetStandaloneAccumulations) {
          entry.splat.colorAccumulatedTranslation = 0
        }
        for (const entry of resetNodeAccumulations) entry.nodeInfo.colorAccumulatedTranslation = 0
        for (const entry of standaloneAccumulations) {
          entry.splat.colorAccumulatedTranslation = entry.next
        }
        for (const entry of nodeAccumulations)
          entry.nodeInfo.colorAccumulatedTranslation = entry.next
        this.lastColorCameraPosition.copy(cameraPosition)
        accumulationsCommitted = true
        if (updatePlans.length > 0) {
          let uploaded = 0
          for (const { splat } of updatePlans) {
            uploaded += Math.max(1, splat.intervalAllocIds.length)
          }
          this.bufferCopyUploadedValue += uploaded
          this.bufferCopyTotalValue = this.allocationMap.size
        }
        if (changedColorAllocIds.size > 0) {
          this.bufferCopyUploadedValue += changedColorAllocIds.size
          this.bufferCopyTotalValue = this.allocationMap.size
        }
        committed = true
      },
      rollback: () => {
        if (finalized) return
        if (accumulationsCommitted) {
          for (const entry of resetStandaloneAccumulations) {
            entry.splat.colorAccumulatedTranslation = entry.previous
          }
          for (const entry of resetNodeAccumulations) {
            entry.nodeInfo.colorAccumulatedTranslation = entry.previous
          }
          for (const entry of standaloneAccumulations) {
            entry.splat.colorAccumulatedTranslation = entry.previous
          }
          for (const entry of nodeAccumulations) {
            entry.nodeInfo.colorAccumulatedTranslation = entry.previous
          }
          this.lastColorCameraPosition.copy(previousCameraPosition)
          accumulationsCommitted = false
        }
        if (baselinesCommitted) {
          for (let index = updatePlans.length - 1; index >= 0; index--) {
            const { splat, plan } = updatePlans[index]
            splat.rollbackUpdateBaseline(plan)
          }
          baselinesCommitted = false
        }
        fullCopyStage?.rollback()
        colorCopyStage?.rollback()
        if (committed) {
          this.bufferCopyUploadedValue = previousBufferCopyUploaded
          this.bufferCopyTotalValue = previousBufferCopyTotal
        }
        committed = false
        prepared = false
      },
      finalize: () => {
        if (!committed || finalized) return
        fullCopyStage?.finalize()
        colorCopyStage?.finalize()
        finalized = true
      },
    }
  }

  resetFrameStats(): void {
    this.bufferCopyUploadedValue = 0
    this.bufferCopyTotalValue = 0
  }

  tickCooldowns(
    logicalFrameToken: number,
    cooldownTicks: number,
    frameSource: object = this
  ): void {
    if (!Number.isSafeInteger(logicalFrameToken) || logicalFrameToken < 0) {
      throw new Error(`非法 GSplat cooldown logical frame token: ${logicalFrameToken}`)
    }
    const octrees = new Set<GSplatOctree<GSplatOctreeFileResource>>()
    for (const instance of this.octreeInstances.values()) octrees.add(instance.octree)
    for (const instance of this.octreeInstancesToDestroy) octrees.add(instance.octree)
    for (const octree of this.retiredOctrees) octrees.add(octree)
    for (const octree of octrees) {
      octree.updateCooldownTick({ source: frameSource, value: logicalFrameToken }, cooldownTicks)
    }
    for (const octree of this.retiredOctrees) {
      if (!octree.hasPendingCooldowns) this.retiredOctrees.delete(octree)
    }
  }

  computeAggregateAabb(): THREE.Box3 | null {
    const state = this.states.get(this.lastVersionValue)
    if (!state) return null
    let aggregate: THREE.Box3 | null = null
    const transformed = new THREE.Box3()
    for (const splat of state.splats) {
      splat.node.updateMatrixWorld(true)
      transformed.copy(splat.aabb).applyMatrix4(splat.node.matrixWorld)
      if (aggregate) aggregate.union(transformed)
      else aggregate = transformed.clone()
    }
    return aggregate
  }

  markInstancesNeedLodUpdate(): void {
    for (const instance of this.octreeInstances.values()) instance.needsLodUpdate = true
  }

  getEnvironmentConfigured(placement: GSplatPlacement): boolean | undefined {
    return this.octreeInstances.get(placement)?.environmentConfigured
  }

  getEnvironmentVisible(placement: GSplatPlacement): boolean | undefined {
    return this.octreeInstances.get(placement)?.environmentVisible
  }

  setEnvironmentVisible(placement: GSplatPlacement, visible: boolean): boolean {
    const instance = this.octreeInstances.get(placement)
    if (!instance) return false
    const changed = instance.setEnvironmentVisible(visible)
    if (changed) {
      this.placementSetDirty = true
      this.placementSetChanged = true
    }
    return changed
  }

  /** Renderer/sorter selection 改变后，旧 render-ready version 不得跨管线复用。 */
  invalidateForRendererSelection(): void {
    this.invalidateRenderReadyState()
  }

  /** 静态资源 device restore 后，强制下一次排序重新 bake 空白的 WorkBuffer。 */
  invalidateForDeviceRestore(): void {
    this.invalidateRenderReadyState()
  }

  releaseState(version: number): void {
    if (version === this.currentVersionValue) return
    const state = this.states.get(version)
    if (!state) return
    this.states.delete(version)
    this.releaseStateResources(state)
    state.destroy()
  }

  destroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    for (const state of this.states.values()) {
      this.releaseStateResources(state)
      state.destroy()
    }
    this.states.clear()
    this.stateResources.clear()
    for (const receipts of this.pendingStateReleases.values()) {
      for (const receipt of receipts) receipt.rollback()
    }
    this.pendingStateReleases.clear()
    let instanceFailure: unknown
    const destroyInstance = (instance: GSplatOctreeInstance<GSplatOctreeFileResource>): void => {
      try {
        instance.destroy()
      } catch (error) {
        instanceFailure ??= error
      }
    }
    for (const instance of this.octreeInstances.values()) destroyInstance(instance)
    for (const instance of this.octreeInstancesToDestroy) destroyInstance(instance)
    for (const lease of this.octreeInstanceLeases.values()) {
      try {
        lease.dispose()
      } catch (error) {
        instanceFailure ??= error
      }
    }
    this.octreeInstances.clear()
    this.octreeInstancesToDestroy.length = 0
    this.octreeInstanceLeases.clear()
    this.retiredOctrees.clear()
    this.workBufferValue.destroy()
    this.allocationMap.clear()
    this.lastStreamedVersion = 0
    this.lastBakedVersion = 0
    if (instanceFailure) throw instanceFailure
  }

  dispose(): void {
    this.destroy()
  }

  private assertActive(): void {
    if (this.destroyed) throw new Error('GSplat World 已销毁')
  }

  private resolvePlacementOctree(
    placement: GSplatPlacement
  ): GSplatOctree<GSplatOctreeFileResource> | null {
    const identity =
      placement.resource instanceof GSplatOctreeResource
        ? placement.resource
        : placement.resource?.resourceIdentity
    if (!(identity instanceof GSplatOctreeResource)) return null
    const octree = identity.octree
    if (!octree) return null
    if (!(octree instanceof GSplatOctree)) {
      throw new Error('GSplat Octree Resource owner 不满足冻结 GSplatOctree 契约')
    }
    return octree as GSplatOctree<GSplatOctreeFileResource>
  }

  private createInstanceInvalidationSink(): GSplatStreamingInvalidationSink {
    return {
      invalidateWorld: (reason) => {
        if (!this.suppressInternalInstanceInvalidation) this.worldStateDirty = true
        this.externalStreamingInvalidation?.invalidateWorld(reason)
      },
      requestFrame: (reason) => this.externalStreamingInvalidation?.requestFrame(reason),
    }
  }

  /** destroyed/null roots 不能进入可回滚 plan；先立即从 active map 隔离。 */
  private sanitizeOctreeInstances(): void {
    for (const [placement, instance] of [...this.octreeInstances]) {
      if (!instance.octree.isDestroyed && isPlacementUsable(placement)) continue
      this.octreeInstances.delete(placement)
      this.octreeInstancesToDestroy.push(instance)
      this.placementSetDirty = true
      this.placementSetChanged = true
    }
  }

  private validateLodConfig(config: GSplatWorldLodConfig): GSplatWorldLodConfig {
    if (!Number.isFinite(config.lodUpdateDistance) || config.lodUpdateDistance < 0) {
      throw new Error(`非法 GSplat lodUpdateDistance: ${config.lodUpdateDistance}`)
    }
    if (!Number.isFinite(config.lodUpdateAngle) || config.lodUpdateAngle < 0) {
      throw new Error(`非法 GSplat lodUpdateAngle: ${config.lodUpdateAngle}`)
    }
    if (!Number.isFinite(config.lodBehindPenalty) || config.lodBehindPenalty < 1) {
      throw new Error(`非法 GSplat lodBehindPenalty: ${config.lodBehindPenalty}`)
    }
    if (!Number.isSafeInteger(config.lodUnderfillLimit) || config.lodUnderfillLimit < 0) {
      throw new Error(`非法 GSplat lodUnderfillLimit: ${config.lodUnderfillLimit}`)
    }
    if (
      config.lodRefinementMode !== undefined &&
      config.lodRefinementMode !== 'step' &&
      config.lodRefinementMode !== 'direct'
    ) {
      throw new Error(`非法 GSplat lodRefinementMode: ${config.lodRefinementMode}`)
    }
    if (!Number.isSafeInteger(config.splatBudget) || config.splatBudget < 0) {
      throw new Error(`非法 GSplat splatBudget: ${config.splatBudget}`)
    }
    return config
  }

  private captureInstanceSignals(
    instance: GSplatOctreeInstance<GSplatOctreeFileResource>
  ): GSplatOctreeInstanceSignals {
    return {
      instance,
      placementLodDirty: instance.placement.lodDirty,
      needsLodUpdate: instance.needsLodUpdate,
      dirtyModifiedPlacements: instance.dirtyModifiedPlacements,
      dirtyPlacementSetChanged: instance.dirtyPlacementSetChanged,
    }
  }

  private clearConsumedInstanceSignals(signal: GSplatOctreeInstanceSignals): void {
    signal.instance.placement.lodDirty = false
    signal.instance.needsLodUpdate = false
    signal.instance.dirtyModifiedPlacements = false
    signal.instance.dirtyPlacementSetChanged = false
  }

  private restoreInstanceSignals(signal: GSplatOctreeInstanceSignals): void {
    signal.instance.placement.lodDirty = signal.placementLodDirty
    signal.instance.needsLodUpdate = signal.needsLodUpdate
    signal.instance.dirtyModifiedPlacements = signal.dirtyModifiedPlacements
    signal.instance.dirtyPlacementSetChanged = signal.dirtyPlacementSetChanged
  }

  private testCameraMovedForLod(camera: THREE.Camera, config: GSplatWorldLodConfig): boolean {
    if (this.octreeInstances.size === 0) return false
    const position = camera.getWorldPosition(new THREE.Vector3())
    if (this.lastLodCameraPosition.distanceTo(position) > config.lodUpdateDistance) return true
    if (config.lodUpdateAngle > 0) {
      if (!Number.isFinite(this.lastLodCameraForward.x)) return true
      const forward = camera.getWorldDirection(new THREE.Vector3())
      const dot = THREE.MathUtils.clamp(this.lastLodCameraForward.dot(forward), -1, 1)
      if (Math.acos(dot) > THREE.MathUtils.degToRad(config.lodUpdateAngle)) return true
    }
    const projectionMarker = camera instanceof THREE.PerspectiveCamera ? camera.fov : 0
    return (
      this.lastLodCameraFov < 0 ||
      Math.abs(projectionMarker - this.lastLodCameraFov) >
        Math.max(0.001, Math.abs(this.lastLodCameraFov) * 0.02)
    )
  }

  private computeGlobalMaxDistance(camera: THREE.Camera): number {
    const cameraPosition = camera.getWorldPosition(new THREE.Vector3())
    const center = new THREE.Vector3()
    const scale = new THREE.Vector3()
    let maxDistance = 0
    for (const instance of this.octreeInstances.values()) {
      instance.placement.node.updateWorldMatrix(true, false)
      instance.placement.aabb.getCenter(center).applyMatrix4(instance.placement.node.matrixWorld)
      instance.placement.node.getWorldScale(scale)
      const radius = instance.placement.aabb.getSize(new THREE.Vector3()).length() * 0.5
      maxDistance = Math.max(
        maxDistance,
        center.distanceTo(cameraPosition) + radius * Math.abs(scale.x)
      )
    }
    return Math.max(maxDistance, 1)
  }

  private computeStaticPaddingEstimate(): number {
    let padding = 0
    for (const placement of this.placements) {
      if (!isPlacementUsable(placement)) continue
      padding += this.rowPadding(this.readPlacementSplatCount(placement))
    }
    return padding
  }

  private rowPadding(count: number): number {
    const width = Math.max(1, this.workBufferValue.textureSize)
    return (width - (count % width)) % width
  }

  private readPlacementSplatCount(placement: GSplatPlacement): number {
    const resource = placement.resource
    if (!resource || !('numSplats' in resource)) return 0
    const count = resource.numSplats
    return typeof count === 'number' && Number.isSafeInteger(count) && count >= 0 ? count : 0
  }

  private collectPlacementEntries(): GSplatWorldPlacementEntry[] {
    const entries: GSplatWorldPlacementEntry[] = this.placements
      .filter(isPlacementUsable)
      .map((placement) => ({
        placement,
        instance: null,
      }))
    for (const instance of this.octreeInstances.values()) {
      if (instance.octree.isDestroyed || !isPlacementUsable(instance.placement)) continue
      for (const placement of instance.activePlacements) {
        if (isPlacementUsable(placement)) entries.push({ placement, instance })
      }
    }
    return entries
  }

  private rollbackOctreeStreamingStage(input: GSplatOctreeStreamingRollback): void {
    let failure: unknown
    const rollback = (operation: () => void): void => {
      try {
        operation()
      } catch (error) {
        failure ??= error
      }
    }
    rollback(() => input.streamingStage.rollback())
    for (let index = input.transitionStages.length - 1; index >= 0; index--) {
      rollback(() => input.transitionStages[index].rollback())
    }
    for (let index = input.evaluationStages.length - 1; index >= 0; index--) {
      rollback(() => input.evaluationStages[index].rollback())
    }
    for (const signal of input.signals) rollback(() => this.restoreInstanceSignals(signal))
    if (
      input.releaseVersion !== null &&
      this.pendingStateReleases.get(input.releaseVersion) === input.releaseReceipts
    ) {
      this.pendingStateReleases.delete(input.releaseVersion)
    }
    for (let index = input.releaseReceipts.length - 1; index >= 0; index--) {
      rollback(() => input.releaseReceipts[index].rollback())
    }
    this.framesTillFullUpdate = input.previousFramesTillFullUpdate
    this.lodUpdateRequested = input.previousLodUpdateRequested
    this.hasNewOctreeInstances = input.previousHasNewInstances
    this.awaitingLodUpdateValue = input.previousAwaitingLodUpdate
    this.budgetScale = input.previousBudgetScale
    this.lastLodCameraPosition.copy(input.previousCameraPosition)
    this.lastLodCameraForward.copy(input.previousCameraForward)
    this.lastLodCameraFov = input.previousCameraFov
    this.worldStateDirty = input.previousWorldStateDirty
    this.placementSetChanged = input.previousPlacementSetChanged
    this.viewLayerMask = input.previousViewLayerMask
    if (failure) throw failure
  }

  private stageInstanceRetirement(
    instance: GSplatOctreeInstance<GSplatOctreeFileResource>
  ): GSplatOctreeReleaseReceipt {
    const octree = instance.octree
    const receipt = instance.stageRetirement()
    let settled = false
    return {
      fileIndices: receipt.fileIndices,
      placementCount: receipt.placementCount,
      commit: () => {
        if (settled) return
        settled = true
        const index = this.octreeInstancesToDestroy.indexOf(instance)
        if (index === -1) {
          receipt.rollback()
          return
        }
        try {
          receipt.commit()
        } finally {
          this.octreeInstancesToDestroy.splice(index, 1)
          if (octree.hasPendingCooldowns) this.retiredOctrees.add(octree)
          this.octreeInstanceLeases.get(instance)?.dispose()
          this.octreeInstanceLeases.delete(instance)
        }
      },
      rollback: () => {
        if (settled) return
        settled = true
        receipt.rollback()
      },
    }
  }

  private stagePlacementStreamingUpdate(
    requireCenters: boolean,
    entries: readonly GSplatWorldPlacementEntry[] = this.placements.map((placement) => ({
      placement,
      instance: null,
    })),
    viewLayerMask = 0xffffffff,
    forceWorldState = false,
    forceFullRebuild = false
  ): GSplatWorldStreamingStage {
    const activeEntries = entries.filter(({ placement }) => isPlacementUsable(placement))
    const trackedPlacements = activeEntries.map((entry) => entry.placement)
    const trackerPlan = this.placementStateTracker.detectChanges(trackedPlacements)
    const visibleEntries = activeEntries.filter(({ placement }) =>
      isPlacementVisibleForLayerMask(placement, viewLayerMask)
    )
    if (!forceWorldState && !this.placementSetDirty && !trackerPlan.changed) {
      return {
        newVersion: false,
        overdrawDirty: false,
        sortNeeded: false,
        commit: () => {},
        rollback: () => {},
        finalize: () => {},
      }
    }

    const nextVersion = this.lastVersionValue + 1
    const previousLastVersion = this.lastVersionValue
    const previousLastStreamedVersion = this.lastStreamedVersion
    const previousPlacementSetDirty = this.placementSetDirty
    const allocatorBefore = this.allocator.createCheckpoint()
    const allocationMapBefore = [...this.allocationMap] as const
    const streamStages: GSplatPlacementInstanceStreamsStage[] = []
    const infos: GSplatInfo[] = []
    let stagedState: GSplatWorldState | null = null
    let allocatorAfter: ReturnType<BlockAllocator['createCheckpoint']> | null = null
    let allocationMapAfter: readonly (readonly [number, MemBlock])[] = []

    try {
      for (const { placement, instance } of visibleEntries) {
        const resource = placement.resource
        if (!resource) continue
        const infoResource = this.requireInfoResource(resource)
        if (requireCenters && !infoResource.centers) continue
        const streamStage = placement.stageInstanceStreams()
        if (streamStage) streamStages.push(streamStage)
        const isOctreeFile = instance !== null && placement.intervals.size > 0
        infos.push(
          new GSplatInfo(
            this.adapter,
            infoResource,
            placement,
            isOctreeFile ? instance.octree.nodes : null,
            isOctreeFile ? instance.nodeInfos : null
          )
        )
      }
      stagedState = new GSplatWorldState(
        nextVersion,
        infos,
        this.allocator,
        this.allocationMap,
        this.adapter.limits.maxTextureDimension2D
      )
      if (forceFullRebuild) stagedState.fullRebuild = true
      allocatorAfter = this.allocator.createCheckpoint()
      allocationMapAfter = Object.freeze([...this.allocationMap])
    } catch (error) {
      for (let index = streamStages.length - 1; index >= 0; index--) {
        streamStages[index].rollback()
      }
      stagedState?.destroy()
      if (!stagedState) {
        for (let index = infos.length - 1; index >= 0; index--) infos[index].destroy()
      }
      this.restoreAllocator(allocatorBefore, allocationMapBefore)
      throw error
    }
    this.restoreAllocator(allocatorBefore, allocationMapBefore)

    const state = stagedState
    const resources = Object.freeze(infos.map((info) => info.resource))
    let acquiredResourceCount = 0
    let streamsCommitted = false
    let allocatorCommitted = false
    let statePublished = false
    let trackerCommitted = false
    let settled = false

    const rollbackCommitted = () => {
      if (trackerCommitted) {
        this.placementStateTracker.rollbackChanges(trackerPlan)
        trackerCommitted = false
      }
      if (statePublished) {
        this.states.delete(nextVersion)
        this.stateResources.delete(state)
        this.lastVersionValue = previousLastVersion
        this.lastStreamedVersion = previousLastStreamedVersion
        this.placementSetDirty = previousPlacementSetDirty
        statePublished = false
      }
      if (allocatorCommitted) {
        this.restoreAllocator(allocatorBefore, allocationMapBefore)
        allocatorCommitted = false
      }
      if (streamsCommitted) {
        for (let index = streamStages.length - 1; index >= 0; index--) {
          streamStages[index].rollback()
        }
        streamsCommitted = false
      } else {
        for (let index = streamStages.length - 1; index >= 0; index--) {
          streamStages[index].rollback()
        }
      }
      for (let index = acquiredResourceCount - 1; index >= 0; index--) {
        resources[index].decRefCount?.()
      }
      acquiredResourceCount = 0
    }

    return {
      newVersion: true,
      overdrawDirty: false,
      sortNeeded: true,
      commit: () => {
        if (settled) throw new Error('GSplat World streaming stage 已结束')
        if (statePublished) return
        try {
          for (const resource of resources) {
            resource.incRefCount?.()
            acquiredResourceCount++
          }
          for (const streamStage of streamStages) streamStage.commit()
          streamsCommitted = true
          this.restoreAllocator(allocatorAfter!, allocationMapAfter)
          allocatorCommitted = true
          this.states.set(nextVersion, state)
          this.stateResources.set(state, resources)
          this.lastVersionValue = nextVersion
          this.lastStreamedVersion = nextVersion
          this.placementSetDirty = false
          statePublished = true
          this.placementStateTracker.commitChanges(trackerPlan)
          trackerCommitted = true
        } catch (error) {
          rollbackCommitted()
          state.destroy()
          settled = true
          throw error
        }
      },
      rollback: () => {
        if (settled) return
        rollbackCommitted()
        state.destroy()
        settled = true
      },
      finalize: () => {
        if (settled) return
        if (!statePublished) {
          rollbackCommitted()
          state.destroy()
          settled = true
          return
        }
        for (const streamStage of streamStages) streamStage.finalize()
        settled = true
      },
    }
  }

  private requireInfoResource(resource: GSplatPlacement['resource']): GSplatInfoResource {
    if (
      !resource ||
      !('id' in resource) ||
      !Number.isSafeInteger(resource.id) ||
      !('numSplats' in resource) ||
      !Number.isSafeInteger(resource.numSplats) ||
      !('centersVersion' in resource) ||
      !Number.isSafeInteger(resource.centersVersion) ||
      !('centers' in resource) ||
      !(resource.aabb instanceof THREE.Box3) ||
      !resource.format
    ) {
      throw new Error('GSplat Placement Resource 不满足 Unified GSplatInfo 契约')
    }
    return resource as GSplatInfoResource
  }

  private restoreAllocator(
    checkpoint: ReturnType<BlockAllocator['createCheckpoint']>,
    allocationMap: readonly (readonly [number, MemBlock])[]
  ): void {
    this.allocator.restoreCheckpoint(checkpoint)
    this.allocationMap.clear()
    for (const [allocId, block] of allocationMap) this.allocationMap.set(allocId, block)
  }

  private releaseStateResources(state: GSplatWorldState): void {
    const resources = this.stateResources.get(state)
    if (!resources) return
    this.stateResources.delete(state)
    for (let index = resources.length - 1; index >= 0; index--) {
      resources[index].decRefCount?.()
    }
  }

  private collectCopyRequirements(
    state: GSplatWorldState,
    version: number
  ): { fullRebuild: boolean; allocIds: ReadonlySet<number>; splats: readonly GSplatWorldSplat[] } {
    let fullRebuild = state.fullRebuild
    const allocIds = new Set(state.needsUploadIds)
    const splats = [...state.needsUpload]
    const seenSplats = new Set(splats)

    for (
      let skippedVersion = this.currentVersionValue + 1;
      skippedVersion < version;
      skippedVersion++
    ) {
      const skipped = this.states.get(skippedVersion)
      if (!skipped) continue
      if (skipped.fullRebuild) fullRebuild = true
      if (fullRebuild) continue
      for (const allocId of skipped.needsUploadIds) {
        if (allocIds.has(allocId)) continue
        allocIds.add(allocId)
        const owner = state.allocIdToSplat.get(allocId)
        if (owner && !seenSplats.has(owner)) {
          seenSplats.add(owner)
          splats.push(owner)
        }
      }
    }
    return { fullRebuild, allocIds, splats }
  }

  private cleanupOldWorldStates(fromVersion: number, newVersion: number): void {
    for (let version = fromVersion; version < newVersion; version++) {
      const state = this.states.get(version)
      if (!state) continue
      this.states.delete(version)
      this.releaseStateResources(state)
      state.destroy()
    }
  }

  private commitPendingStateReleases(newVersion: number): void {
    let failure: unknown
    const versions = [...this.pendingStateReleases.keys()]
      .filter((version) => version <= newVersion)
      .sort((left, right) => left - right)
    for (const version of versions) {
      const receipts = this.pendingStateReleases.get(version)
      if (!receipts) continue
      this.pendingStateReleases.delete(version)
      for (const receipt of receipts) {
        try {
          receipt.commit()
        } catch (error) {
          failure ??= error
        }
      }
    }
    if (failure) throw failure
  }

  private stageFormat(format: GSplatFormat): GSplatWorkBufferFormatStage | null {
    const workBufferStage = this.workBufferValue.stageFormat(format)
    if (!workBufferStage) return null

    const previousCurrentVersion = this.currentVersionValue
    const previousLastBakedVersion = this.lastBakedVersion
    const previousLastStreamedVersion = this.lastStreamedVersion
    const previousSorted = [...this.states].map(
      ([version, state]) => [version, state.sortedBefore] as const
    )
    let committed = false
    let settled = false
    return {
      format,
      commit: () => {
        if (settled) throw new Error('GSplat World format stage 已结束')
        if (committed) return
        workBufferStage.commit()
        this.invalidateRenderReadyState()
        committed = true
      },
      rollback: () => {
        if (settled) return
        let firstError: unknown
        try {
          workBufferStage.rollback()
        } catch (error) {
          firstError = error
        }
        if (committed) {
          this.currentVersionValue = previousCurrentVersion
          this.lastBakedVersion = previousLastBakedVersion
          this.lastStreamedVersion = previousLastStreamedVersion
          for (const [version, sortedBefore] of previousSorted) {
            const state = this.states.get(version)
            if (state) state.sortedBefore = sortedBefore
          }
        }
        committed = false
        settled = true
        if (firstError) throw firstError
      },
      finalize: () => {
        if (settled) return
        settled = true
        workBufferStage.finalize()
      },
    }
  }

  private invalidateRenderReadyState(): void {
    this.assertActive()
    this.currentVersionValue = 0
    this.lastBakedVersion = 0
    this.lastStreamedVersion = 0
    for (const state of this.states.values()) state.sortedBefore = false
  }
}

function isPlacementUsable(placement: GSplatPlacement): boolean {
  return !placement.isDestroyed && Boolean(placement.resource)
}

function isPlacementNodeVisible(placement: GSplatPlacement): boolean {
  if (!isPlacementUsable(placement)) return false
  let node: { readonly visible: boolean; readonly parent: unknown } | null = placement.node
  while (node) {
    if (!node.visible) return false
    node = node.parent as typeof node
  }
  return true
}

function isPlacementVisibleForLayerMask(
  placement: GSplatPlacement,
  viewLayerMask: number
): boolean {
  return isPlacementNodeVisible(placement) && (placement.node.layers.mask & viewLayerMask) !== 0
}
